import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { AlertCircle, CheckCircle2, ExternalLink, FileCheck2, FolderOpen, RefreshCw, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  type EvidenceStatus,
  formatFramework,
  useCompliancePrograms,
  useProgramEvidence,
} from "@/hooks/use-compliance-programs";

const statusLabels: Record<EvidenceStatus, string> = {
  missing: "Missing",
  uploaded: "Uploaded",
  linked: "Linked",
  expired: "Expired",
  accepted: "Accepted",
};

function statusVariant(status: EvidenceStatus) {
  if (["accepted", "uploaded", "linked"].includes(status)) return "default";
  if (status === "expired") return "destructive";
  return "outline";
}

export default function Evidence() {
  const { toast } = useToast();
  const { programs } = useCompliancePrograms();
  const [programFilter, setProgramFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState<EvidenceStatus | "all">("all");
  const [search, setSearch] = useState("");
  const { evidence, loading, error, updateEvidenceItem } = useProgramEvidence(programFilter);

  const filteredEvidence = useMemo(() => {
    const query = search.trim().toLowerCase();
    return evidence.filter((item) => {
      if (statusFilter !== "all" && item.status !== statusFilter) return false;
      if (!query) return true;
      return [
        item.title,
        item.description,
        item.evidence_type,
        item.program?.name,
        item.framework_control?.control_id,
        item.framework_control?.title,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [evidence, search, statusFilter]);

  const grouped = useMemo(() => {
    return filteredEvidence.reduce<Record<string, typeof filteredEvidence>>((acc, item) => {
      const key = `${item.program?.name ?? "Program"} / ${item.framework_control?.control_id ?? "Control"}`;
      acc[key] = acc[key] ?? [];
      acc[key].push(item);
      return acc;
    }, {});
  }, [filteredEvidence]);

  const readyCount = evidence.filter((item) => ["uploaded", "linked", "accepted"].includes(item.status)).length;
  const readiness = evidence.length ? Math.round((readyCount / evidence.length) * 100) : 0;

  const handleStatus = async (itemId: string, status: EvidenceStatus) => {
    try {
      await updateEvidenceItem(itemId, { status });
      toast({ title: "Evidence updated", description: `Status changed to ${statusLabels[status]}.` });
    } catch (err) {
      toast({
        title: "Could not update evidence",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-32" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-3xl font-medium tracking-tight">Evidence</h1>
          <p className="text-muted-foreground">Track required artifacts by compliance program, control, owner, due date, and reuse status.</p>
        </div>
        <Button variant="outline" onClick={() => window.location.reload()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {error && <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">{error}</div>}

      <Card>
        <CardContent className="grid gap-4 p-5 lg:grid-cols-[1fr_320px] lg:items-center">
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Evidence readiness</span>
              <span className="font-medium">{readiness}%</span>
            </div>
            <Progress value={readiness} className="h-3" />
            <p className="text-sm text-muted-foreground">
              {readyCount}/{evidence.length} evidence items are uploaded, linked, or accepted.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2 text-center text-sm">
            <div className="rounded-md bg-muted/50 p-3">
              <p className="text-muted-foreground">Missing</p>
              <p className="mt-1 text-xl font-semibold">{evidence.filter((item) => item.status === "missing").length}</p>
            </div>
            <div className="rounded-md bg-muted/50 p-3">
              <p className="text-muted-foreground">Expired</p>
              <p className="mt-1 text-xl font-semibold">{evidence.filter((item) => item.status === "expired").length}</p>
            </div>
            <div className="rounded-md bg-muted/50 p-3">
              <p className="text-muted-foreground">Accepted</p>
              <p className="mt-1 text-xl font-semibold">{evidence.filter((item) => item.status === "accepted").length}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="grid gap-3 p-4 lg:grid-cols-[1fr_220px_180px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search evidence, controls, or programs..." className="pl-9" />
          </div>
          <Select value={programFilter} onValueChange={setProgramFilter}>
            <SelectTrigger>
              <SelectValue placeholder="Program" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All programs</SelectItem>
              {programs.map((program) => (
                <SelectItem key={program.id} value={program.id}>{program.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as EvidenceStatus | "all")}>
            <SelectTrigger>
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(statusLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {filteredEvidence.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <FolderOpen className="mx-auto mb-3 h-10 w-10 opacity-50" />
            No evidence items match this view.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([group, items]) => {
            const first = items[0];
            return (
              <Card key={group}>
                <CardHeader className="pb-3">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <CardTitle className="flex flex-wrap items-center gap-2 text-base">
                        <Badge variant="outline">{first.framework_control?.control_id ?? "Control"}</Badge>
                        {first.framework_control?.title ?? group}
                      </CardTitle>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {first.program?.name} · {first.program?.framework ? formatFramework(first.program.framework) : ""}
                      </p>
                    </div>
                    {first.program_id && (
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/compliance/programs/${first.program_id}`}>
                          <ExternalLink className="mr-2 h-3.5 w-3.5" />
                          Program
                        </Link>
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {items.map((item) => (
                    <div key={item.id} className="grid gap-3 rounded-md border p-4 lg:grid-cols-[1fr_180px_180px] lg:items-center">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-medium">{item.title}</p>
                          {item.recurrence && <Badge variant="secondary">{item.recurrence}</Badge>}
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{item.description}</p>
                        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                          <span>{item.owner?.name ?? "Unassigned"}</span>
                          <span>{item.due_date ? `Due ${format(parseISO(item.due_date), "MMM d, yyyy")}` : "No due date"}</span>
                          {item.knowledge_document && <span className="flex items-center gap-1"><FileCheck2 className="h-3 w-3" />{item.knowledge_document.name}</span>}
                        </div>
                      </div>
                      <Badge variant={statusVariant(item.status)} className="w-fit">{statusLabels[item.status]}</Badge>
                      <Select value={item.status} onValueChange={(value) => handleStatus(item.id, value as EvidenceStatus)}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(statusLabels).map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              <span className="flex items-center gap-2">
                                {value === "accepted" ? <CheckCircle2 className="h-3.5 w-3.5" /> : value === "expired" ? <AlertCircle className="h-3.5 w-3.5" /> : null}
                                {label}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
